import { useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { SignUp } from '../SignUp/SignUp';
import {
  useCompanyIdByProjectQuery,
  useGetProjectSamlAuthInfoQuery,
} from '@/generated/graphql';
import {
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import { useTranslation } from 'react-i18next';
import { PageLoader } from '@/components/Elements/PageLoader/PageLoader';
import { useRedirect } from '@/hooks/useRedirect';
import { useChangeLocale } from '@/hooks/useChangeLocale';

import { SignUpSSO } from '../SignUpSSO/SignUpSSO';
import { setIndirectInvParamsToLocalStorage } from '@/utils/auth';
import { INDIRECT_INV_KEYS } from '@/constants/indirectInvite';

export function IndirectInvite() {
  const changeLocale = useChangeLocale();
  const redirectFromLocalStorage = useRedirect();
  const { t } = useTranslation();
  const { handleMsgType } = useNotification();
  const urlParams = useParams();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('project');
  const stageId = searchParams.get('stageId');
  const locale = searchParams.get('locale');
  const signedIn = searchParams.get('signedIn');
  const projectSubId = searchParams.get('projectSubId');
  const hiringManagers = searchParams.get('hms');

  useEffect(() => {
    setIndirectInvParamsToLocalStorage({
      projectId,
      stageId,
      locale,
      projectSubId,
      hms: hiringManagers,
    });
  }, [projectId, stageId, locale, projectSubId, hiringManagers]);

  useEffect(() => {
    //Change locale if it is provided as a query param
    if (locale) {
      changeLocale(locale);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  const getIndirectInvParams = () => {
    const params: string[] = [];
    searchParams?.forEach((value, key) => {
      if (INDIRECT_INV_KEYS.includes(key)) {
        params.push(`${key}=${value}`);
      }
    });

    return params.join('&');
  };

  const redirectWithIndirectInvParams = () => {
    redirectFromLocalStorage({
      redirectPath: '/inv?' + getIndirectInvParams(),
    });
  };

  useEffect(() => {
    if (signedIn) {
      redirectWithIndirectInvParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn]);

  const { data } = useCompanyIdByProjectQuery({
    skip: !projectId,
    variables: { id: parseInt(projectId as string) },
    onError: error => {
      handleMsgType({
        type: TNotification.error,
        title: t('common.failed'),
        message: error.message,
      });
    },
  });

  const { data: projectSamlAuthInfo } = useGetProjectSamlAuthInfoQuery({
    variables: {
      id: projectId ? parseInt(projectId) : 0,
    },
  });

  const samlSubId = projectSamlAuthInfo?.getProjectSamlAuthInfo;

  const companyId = data?.getCompanyId.companyId;

  const onSignupSuccess = () => {
    redirectWithIndirectInvParams();
  };

  // if the SSO is enabled redirect to SSO only view (currently only Merlin is supported)
  if (samlSubId) {
    return <SignUpSSO subId={samlSubId} projectId={projectId || undefined} />;
  }

  return (
    <>
      {companyId && projectId && !signedIn ? (
        <SignUp
          locale={locale || urlParams.lang || 'en'}
          projectId={parseInt(projectId!) || undefined}
          projectSubId={projectSubId || undefined}
          companyId={companyId}
          onSignupSuccess={onSignupSuccess}
          signInLink={window.location.search}
        />
      ) : (
        <PageLoader />
      )}
    </>
  );
}
